import { ActionIcon, Checkbox, Combobox, Group, Input, ScrollArea, TextInput, useCombobox } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import styles from "./TransferList.module.css";

export type TransferListItem = {
  label: string;
  value: string;
};

type TransferListProps = {
  options?: TransferListItem[];
  searchLabel: string;
  defaultValue?: string[];
  onChange: (values: string[]) => void;
  maxHeight?: number;
  targetTitle?: string;
  sourceTitle?: string;
};

export function TransferList({
  options = [],
  defaultValue = [],
  searchLabel,
  onChange,
  maxHeight,
  targetTitle,
  sourceTitle,
}: TransferListProps) {
  const data: [TransferListItem[], TransferListItem[]] = useMemo(
    () => [
      options.filter((o) => defaultValue.find((v) => v === o.value)),
      options.filter((o) => defaultValue.find((v) => v === o.value) === undefined),
    ],
    [options, defaultValue],
  );

  const handleTransfer = (transferFrom: number, options: string[]) => {
    const transferTo = transferFrom === 0 ? 1 : 0;
    const transferFromData = data[transferFrom].filter((item) => !options.includes(item.value));
    const transfered = options.map((option) => data[transferFrom].find((v) => v.value === option)).filter((option) => option !== undefined);

    const transferToData = [...data[transferTo], ...transfered];

    const result: [TransferListItem[], TransferListItem[]] = [[], []];
    result[transferTo] = transferToData;
    result[transferFrom] = transferFromData;
    onChange(result[0].map((item) => item.value));
  };

  return (
    <div className={styles.root}>
      <RenderList
        searchLabel={searchLabel}
        type="forward"
        options={data[0]}
        onTransfer={(options) => handleTransfer(0, options)}
        maxheight={maxHeight}
        title={targetTitle}
      />
      <RenderList
        searchLabel={searchLabel}
        type="backward"
        options={data[1]}
        maxheight={maxHeight}
        onTransfer={(options) => handleTransfer(1, options)}
        title={sourceTitle}
      />
    </div>
  );
}

type RenderListProps = {
  options?: TransferListItem[];
  onTransfer: (options: string[]) => void;
  type: "forward" | "backward";
  searchLabel: string;
  maxheight?: number;
  title?: string;
};

function RenderList({ options = [], onTransfer, type, searchLabel, maxheight = 200, title }: RenderListProps) {
  const combobox = useCombobox();
  const [value, setValue] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const handleValueSelect = (val: string) =>
    setValue((current) => (current.includes(val) ? current.filter((v) => v !== val) : [...current, val]));

  const items = options
    .filter((item) => item.label.toLowerCase().includes(search.toLowerCase().trim()))
    .map((item) => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={value.includes(item.value)}
        onMouseOver={() => combobox.resetSelectedOption()}
      >
        <Group gap="sm">
          <Checkbox checked={value.includes(item.value)} aria-hidden tabIndex={-1} onChange={() => {}} style={{ pointerEvents: "none" }} />
          <span>{item.label}</span>
        </Group>
      </Combobox.Option>
    ));

  return (
    <div className={styles.renderList} data-type={type}>
      <Input.Label>{title}</Input.Label>
      <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
        <Combobox.EventsTarget>
          <Group wrap="nowrap" gap={0} className={styles.controls}>
            <TextInput
              placeholder={searchLabel}
              classNames={{ input: styles.input }}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                combobox.updateSelectedOptionIndex();
              }}
              w={"28rem"}
            />
            <ActionIcon
              radius={0}
              variant="default"
              size={36}
              w={"2rem"}
              className={styles.control}
              onClick={() => {
                onTransfer(value);
                setValue([]);
              }}
            >
              <IconChevronRight className={styles.icon} />
            </ActionIcon>
          </Group>
        </Combobox.EventsTarget>

        <div className={styles.list}>
          <Combobox.Options>
            <ScrollArea.Autosize type="scroll" mah={maxheight}>
              {items.length > 0 ? items : <Combobox.Empty>Nichts gefunden</Combobox.Empty>}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
}
